import { makeStyles } from '@material-ui/styles'
import { listTenants } from 'account/components/userManagement/tenants/new-actions'
import { tenantsSelector } from 'account/components/userManagement/tenants/selectors'
import { ArrayElement } from 'core/actions/Action'
import SelectableCard from 'core/components/SelectableCard'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import PicklistField from 'core/components/validatedForm/DropdownField'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Button from 'core/elements/button/Button'
import Grid from 'core/elements/grid'
import { GridViewColumn } from 'core/elements/grid/Grid'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import Theme from 'core/themes/model'
import { nonDecimalValueValidator, nonZeroValidator } from 'core/utils/fieldValidators'
import { routeManager } from 'core/utils/routes'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import {
  getResources,
  listHostAggregates,
  listHypervisors,
  listResmgrHosts,
  updateResourceTraits,
  updateTrait,
} from 'openstack/components/infrastructure/actions'
import { HostsCellComponent } from 'openstack/components/infrastructure/host-aggregates/HostAggregatesListPage'
import {
  allGpuProfilesByModel,
  hostAggregatesSelector,
  hypervisorsSelector,
  resmgrHostsByGpuModelAndProfile,
} from 'openstack/components/infrastructure/selectors'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { addFlavorTenant, createFlavor, listFlavors, updateMetadata } from './actions'
import GpuPassthroughModelPicklist from './GpuPassthroughModelPicklist'
import { flavorsByIdSelector } from './selectors'
import UnitPicklist from './UnitPicklist'
import VgpuModelPicklist from './VgpuModelPicklist'
import VgpuProfilePicklist from './VgpuProfilePicklist'
import Tooltip from 'core/elements/tooltip'
import Badge from 'core/elements/badge'
import { isHotpluggableFlavor } from 'core/utils/helpers'
import SizeInputWithUnit, { Unit } from 'core/components/SizeInputWithUnit'
import { doAllKeysAndValuesMatch } from 'app/plugins/openstack/helpers'

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    display: 'grid',
    gap: 16,
    marginBottom: 32,
  },
  withUnits: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  unitsDropdown: {
    position: 'relative',
    bottom: 1,
  },
  sameLine: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
  },
  splitLine: {
    display: 'grid',
    alignItems: 'center',
    gap: 16,
    gridTemplateColumns: '1fr 1fr',
  },
  separator: {
    height: 1,
    width: '100%',
    background: theme.palette.grey[200],
    margin: '16px 0px',
  },
  gpuCards: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gap: 8,
  },
  card: {
    padding: 8,
    alignItems: 'center',
  },
  cardContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))

const capitalizeAndUnderscore = (str) => {
  return str
    .toUpperCase()
    .replaceAll('-', '_')
    .replaceAll(' ', '_')
}

const gpuModes = [
  { label: 'Passthrough', value: 'passthrough' },
  { label: 'vGPU', value: 'vgpu' },
]

const tenantColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'description',
    label: 'Description',
  },
]

const ramOptions = [
  { label: 'MiB', value: 'MiB' as Unit },
  { label: 'GiB', value: 'GiB' as Unit },
]

const diskOptions = [
  { label: 'GiB', value: 'GiB' as Unit },
  { label: 'TiB', value: 'TiB' as Unit },
]

type SelectorModel = ArrayElement<ReturnType<typeof hostAggregatesSelector>>

interface Props {
  addRoute: Route
}

export default function CreateFlavorModal({ addRoute }: Props) {
  const { history, location } = useReactRouter()
  const urlParams = new URLSearchParams(location.search)
  const flavorToClone = urlParams.get('id')

  const classes = useStyles()
  const defaultParams = {
    name: '',
    // description: '',
    vcpus: 2,
    disk: 20,
    ram: 4096,
    ramUnit: 'GiB',
    diskUnit: 'GiB',
    public: true,
    metadata: [],
    tenants: [],
    enableHotplugging: false,
    enableGpu: false,
    gpuType: 'none',
    gpuPassthroughModel: null,
    gpuPassthroughNumber: 1,
    vgpuNumber: 1,
    vgpuModel: '',
    vgpuProfile: '',
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)
  const [showMetadata, setShowMetadata] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [showAllHostAggregates, setShowAllHostAggregates] = useState(false)
  const [hasPrefilled, setHasPrefilled] = useState(false)

  const { loading } = useListAction(listFlavors)
  const flavorsById = useSelector(flavorsByIdSelector)

  useEffect(() => {
    if (flavorToClone && flavorsById[flavorToClone] && !hasPrefilled) {
      const flavor = flavorsById[flavorToClone]
      updateParams({
        name: `${flavor.name}-clone`,
        ram: flavor.ram,
        ramUnit: 'MiB',
        vcpus: flavor.vcpus,
        disk: flavor.disk,
        diskUnit: 'GiB',
        public: flavor['os-flavor-access:is_public'],
        enableHotplugging: isHotpluggableFlavor(flavor),
        metadata: Object.entries(flavor.extra_specs).map(([key, value]) => {
          return { key, value }
        }),
      })
      setShowMetadata(false)
      setHasPrefilled(true)
    }
  }, [flavorToClone, flavorsById, hasPrefilled])

  const { loading: loadingTenants } = useListAction(listTenants)
  const tenantsList = useSelector(tenantsSelector)

  useListAction(listResmgrHosts, { params })
  const hostsByGpuModelAndProfile = useSelector(resmgrHostsByGpuModelAndProfile)

  useListAction(listHypervisors)
  const hypervisors = useSelector(hypervisorsSelector)

  const gpuProfilesByModel = useSelector(allGpuProfilesByModel)

  const { message, loading: loadingHostAggregates, reload } = useListAction(listHostAggregates, {
    params,
  })
  const hostAggregates = useSelector(hostAggregatesSelector)

  const hostAggregatesWithMatchFlag = useMemo(() => {
    const paramMap = new Map(params?.metadata?.map((pair) => [pair?.key, pair?.value]))

    return hostAggregates.map((hostAggregate) => ({
      ...hostAggregate,
      isMatchingHostAggregates: doAllKeysAndValuesMatch(
        hostAggregate?.metadata,
        Object.fromEntries(paramMap),
      ),
    }))
  }, [hostAggregates, params.metadata])

  const filteredHostAggregates = useMemo(() => {
    if (!params.metadata.length || !params.metadata[0]?.key || !params.metadata[0]?.value) {
      return hostAggregatesWithMatchFlag
    }
    return hostAggregatesWithMatchFlag.filter((host) => host.isMatchingHostAggregates)
  }, [hostAggregatesWithMatchFlag])

  const { update, updating, error, reset } = useUpdateAction(createFlavor)
  const { update: updateMetadataFn, reset: resetUpdateMetadata } = useUpdateAction(updateMetadata)
  const { update: addTenantFn, reset: resetAddTenant } = useUpdateAction(addFlavorTenant)

  const submitForm = useCallback(async () => {
    setSubmitting(true)
    const body = {
      flavor: {
        name: params.name,
        vcpus: params.vcpus,
        disk: params.disk,
        ram: params.ram,
        'os-flavor-access:is_public': params.public,
        // description: params.description,
      },
    }
    const { success, response } = await update({ body })
    if (!success) {
      setSubmitting(false)
      return
    }
    const flavorId = response.id
    if (!params.public) {
      const promises = params.tenants.map((tenant) => {
        const tenantBody = {
          addTenantAccess: {
            tenant: tenant.id,
          },
        }
        return addTenantFn({ id: flavorId, body: tenantBody })
      })
      await Promise.allSettled(promises)
    }
    if (params.metadata.length || params.enableGpu) {
      const metadata = params.metadata.reduce((accum, pair) => {
        return {
          ...accum,
          [pair.key]: pair.value,
        }
      }, {})
      const passthroughMetadata =
        params.gpuType === 'passthrough'
          ? {
              'pci_passthrough:alias': `${params.gpuPassthroughModel}:${params.gpuPassthroughNumber}`,
            }
          : {}
      const vgpuMetadata =
        params.gpuType === 'vgpu'
          ? {
              // [`trait:CUSTOM_${capitalizeAndUnderscore(params.vgpuProfile)}`]: 'required',
              'resources:VGPU': params.vgpuNumber,
            }
          : {}
      const metadataBody = {
        addBody: {
          extra_specs: {
            ...metadata,
            ...passthroughMetadata,
            ...vgpuMetadata,
          },
        },
      }
      await updateMetadataFn({ id: flavorId, body: metadataBody })
    }

    /**
     * If vGPU is enabled, add the node is vGPU
     * Find the hosts with matching gpuModel and selected gpuProfiles
     * Find the hypervisors with matching host
     * Get All the resources for the hypervisors
     * Find the maching resources for the hypervisors hostnames
     * Add the trait to the resources
     */
    // TODO - Removing this for June release, to fix this issue - https://platform9.atlassian.net/browse/PCD-2554
    // if (params.enableGpu && params.gpuType === 'vgpu') {
    //   const vGPUProfile = gpuProfilesByModel[params.vgpuModel].find(
    //     (profile) => profile['Profile name'] === params.vgpuProfile,
    //   )
    //   const placementResources = await getResources()
    //   const hostsWithProfile = hostsByGpuModelAndProfile[params.vgpuModel][vGPUProfile?.name]

    //   const matchingHypervisors = hypervisors.filter((hypervisor) =>
    //     hostsWithProfile.includes(hypervisor?.service?.host),
    //   )
    //   const matchingResources = placementResources.filter((resource) =>
    //     matchingHypervisors.find((hypervisor) =>
    //       resource.name.includes(hypervisor.hypervisor_hostname),
    //     ),
    //   )

    //   const trait = `CUSTOM_${capitalizeAndUnderscore(params.vgpuProfile)}`
    //   await updateTrait({ trait })
    //   await Promise.allSettled(
    //     matchingResources.map((resource) =>
    //       updateResourceTraits({ trait, resourceId: resource.uuid }),
    //     ),
    //   )
    // }
    handleClose()
  }, [params, hypervisors])

  const handleClose = () => {
    setParams(defaultParams)
    setSubmitting(false)
    reset()
    resetUpdateMetadata()
    resetAddTenant()
    setHasPrefilled(false)
    history.push(routeManager.routes.openstack.flavors.path())
  }

  const handleMetadataChange = (cb) => {
    setShowMetadata(false)
    cb()
    // setShowMetadata(true)
  }

  useEffect(() => {
    if (!showMetadata) {
      setShowMetadata(true)
    }
  }, [showMetadata])

  const hostAggregateColumns: GridViewColumn<SelectorModel>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        width: 'medium',
      },
      // {
      //   key: 'id',
      //   label: 'ID',
      //   display: false,
      // },
      // {
      //   key: 'availability_zone',
      //   label: 'Availability Zone',
      // },
      {
        key: 'hostInfo',
        label: 'Hosts',
        CellComponent: HostsCellComponent,
      },
      // {
      //   key: 'usage',
      //   label: 'Resource Utilization',
      //   CellComponent: StatsCell,
      // },
      {
        key: 'metadata',
        label: 'Metadata',
        disableSorting: true,
        CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
      },
      {
        key: 'id',
        label: '',
        CellComponent: ({ value, item }) => {
          const metadataKeys = Object.keys(item?.metadata || {})
          const metadata = metadataKeys.map((key) => {
            return {
              key,
              value: item?.metadata?.[key],
            }
          })
          const hasMetadata = metadataKeys?.length > 0

          return (
            <Tooltip
              message={
                !hasMetadata
                  ? 'No Metadata Available'
                  : item?.isMatchingHostAggregates
                  ? 'Already Assigned'
                  : ''
              }
            >
              <Button
                onClick={() => {
                  if (hasMetadata) {
                    handleMetadataChange(() =>
                      // make sure to keep the existing metadata & avoid adding the same metadata again
                      updateParams({
                        metadata: [
                          ...(params?.metadata || []).filter(
                            (existing) => !metadataKeys?.includes(existing?.key),
                          ),
                          ...metadata,
                        ],
                      }),
                    )
                  }
                }}
                disabled={!hasMetadata || item?.isMatchingHostAggregates}
              >
                Assign Metadata
              </Button>
            </Tooltip>
          )
        },
      },
    ],
    [params.metadata],
  )

  // Reset vCPUs and RAM when hotplugging is disabled
  useEffect(() => {
    if (params.enableHotplugging) {
      updateParams({ vcpus: 0, ram: 0 })
    } else {
      updateParams({ vcpus: 2, ram: 4096 })
    }
  }, [params.enableHotplugging])

  return (
    <ModalForm
      route={addRoute}
      title={`Create Flavor`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={submitting}
      error={error}
      submitTitle={`Create Flavor`}
      maxWidth={1000}
    >
      <div className={classes.fields}>
        <FormFieldSection title="Flavor Configuration">
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            // info="Choose a name for your flavor"
            required
          />
          <CheckboxField
            id="enableHotplugging"
            value={params.enableHotplugging}
            label="Enable Hotplugging"
            onChange={getParamsUpdater('enableHotplugging')}
            info="Enable the ability to hotplug VM memory and CPU. CPU and memory must be set to 0 when hotplugging is enabled."
          />
          {/* Part of nova v2.55+ */}
          {/* <TextField
            id="description"
            label="Description"
            onChange={getParamsUpdater('description')}
            value={params.description}
          /> */}
          <TextField
            id="vcpus"
            label="vCPUs"
            // @ts-ignore complaint about string vs number
            onChange={getParamsUpdater('vcpus')}
            value={params.vcpus}
            type="number"
            min={1}
            validations={
              !params.enableHotplugging ? [nonZeroValidator(), nonDecimalValueValidator()] : []
            }
            // info="Number of vCPUs the flavor should have"
            disabled={params.enableHotplugging}
            required={!params.enableHotplugging}
          />
          <SizeInputWithUnit
            id="ram"
            label="RAM"
            onChange={getParamsUpdater('ram')}
            value={params.ram}
            min={1}
            resetValueToZero={params.enableHotplugging}
            validations={
              !params.enableHotplugging ? [nonZeroValidator(), nonDecimalValueValidator()] : []
            }
            disabled={params.enableHotplugging}
            required={!params.enableHotplugging}
            items={ramOptions}
          />
          <SizeInputWithUnit
            id="disk"
            label="Disk"
            onChange={getParamsUpdater('disk')}
            value={params.disk}
            min={0}
            outputValueUnit="GiB"
            required
            validations={[nonDecimalValueValidator()]}
            items={diskOptions}
          />
        </FormFieldSection>
        <div className={classes.separator} />
        <ToggleSwitchField
          id="enableGpu"
          value={params.enableGpu}
          label={<Text variant="subtitle2">Enable GPU</Text>}
          onChange={getParamsUpdater('enableGpu')}
          tooltipMsg="If enabled, VMs created with this flavor will be created on GPU hosts."
        />
        {params.enableGpu && (
          <FormFieldSection title="">
            <Text variant="subtitle2">GPU Mode</Text>
            <Text variant="body2">
              Choose the type of GPU support you want this flavor to provide. Use <b>Passthrough</b>{' '}
              to assign full physical GPUs, or <b>vGPU</b> to use virtual GPU slices.
            </Text>
            <div className={classes.gpuCards}>
              {gpuModes?.map(({ label, value }) => (
                <SelectableCard
                  id={value}
                  onClick={() => updateParams({ gpuType: value })}
                  active={params?.gpuType === value}
                  withCustomBody
                  showRadioButton
                  className={classes.card}
                  // disabled={disabled}
                  // showCheckmarkIcon
                >
                  <div className={classes.cardContent}>
                    <Text variant="caption1">{label}</Text>
                    {value === 'vgpu' && <Badge variant="warning" text="Beta" />}
                  </div>
                </SelectableCard>
              ))}
              <div />
            </div>
            {params?.gpuType === 'passthrough' && (
              <div className={classes.splitLine}>
                <PicklistField
                  DropdownComponent={GpuPassthroughModelPicklist}
                  id="gpuPassthroughModel"
                  onChange={getParamsUpdater('gpuPassthroughModel')}
                  value={params.gpuPassthroughModel}
                  label="GPU Model"
                  required
                />
                <TextField
                  id="gpuPassthroughNumber"
                  label="Number of GPUs"
                  // @ts-ignore complaint about string vs number
                  onChange={getParamsUpdater('gpuPassthroughNumber')}
                  value={params.gpuPassthroughNumber}
                  type="number"
                  min={1}
                  required
                />
              </div>
            )}
            {params?.gpuType === 'vgpu' && (
              <div className={classes.splitLine}>
                <TextField
                  id="vgpuNumber"
                  label="Number of vGPUs"
                  // @ts-ignore complaint about string vs number
                  onChange={getParamsUpdater('vgpuNumber')}
                  value={params.vgpuNumber}
                  type="number"
                  min={1}
                  required
                />
                {/* <PicklistField
                  DropdownComponent={VgpuModelPicklist}
                  id="vgpuModel"
                  onChange={getParamsUpdater('vgpuModel')}
                  value={params.vgpuModel}
                  label="GPU Model"
                  required
                />
                <PicklistField
                  DropdownComponent={VgpuProfilePicklist}
                  id="vgpuProfile"
                  onChange={getParamsUpdater('vgpuProfile')}
                  value={params.vgpuProfile}
                  label="vGPU Profile"
                  gpuModel={params.vgpuModel}
                  required
                /> */}
                <div />
                {params.vgpuModel &&
                  (!gpuProfilesByModel?.[params.vgpuModel] ||
                    gpuProfilesByModel?.[params.vgpuModel]?.length === 0) && (
                    <ErrorMessage>No vGPU profiles available</ErrorMessage>
                  )}
              </div>
            )}
          </FormFieldSection>
        )}
        <div className={classes.separator} />
        <FormFieldSection title="Flavor Metadata">
          <Text variant="body2">
            You can restrict placement of Virtual machines created with this flavor to certain
            hosts.
          </Text>
          <Text variant="body2">
            Virtual machines created using this flavor will only be assigned to hosts whose metadata
            matches all of the flavor's metadata.
          </Text>
          {showMetadata && (
            <KeyValuesField
              id="metadata"
              label="Metadata"
              value={params.metadata}
              onChange={getParamsUpdater('metadata')}
              addLabel="Add Metadata"
              allowMultipleValues
            />
          )}
          <div className={classes.sameLine}>
            <Text variant="caption1">Matching Host Aggregates</Text>
            <ToggleSwitch
              active={showAllHostAggregates}
              onClick={(val) => setShowAllHostAggregates(val)}
              label="Show All"
            />
          </div>
          <Grid
            uniqueIdentifier="id"
            data={showAllHostAggregates ? hostAggregatesWithMatchFlag : filteredHostAggregates}
            columns={hostAggregateColumns}
            loading={loadingHostAggregates}
            compact
            disableToolbar
          />
        </FormFieldSection>
        <FormFieldSection title="Tenant Access">
          <CheckboxField
            id="public"
            label="Make Public"
            value={params.public}
            onChange={getParamsUpdater('public')}
            info="If checked, flavor will be available for every tenant."
          />
          {!params.public && (
            <>
              <Text variant="body2">
                Select the tenants that should have access to this flavor.
              </Text>
              <ListTableField
                id="tenants"
                data={tenantsList}
                loading={loadingTenants}
                columns={tenantColumns}
                onChange={getParamsUpdater('tenants')}
                value={params.tenants}
                uniqueIdentifier="id"
                searchTargets={['name']}
                multiSelection
              />
            </>
          )}
        </FormFieldSection>
      </div>
    </ModalForm>
  )
}
